import React from "react";
import { useTranslation } from "react-i18next";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";

export interface ThresholdSegmentedProps {
  /** Current threshold (Infinity = "∞" = show all) */
  threshold: number;
  onChange: (n: number) => void;
  /** Quick choices, in order. Last item should usually be Infinity. */
  options?: number[];
}

const DEFAULT_OPTIONS = [2, 3, 5, Number.POSITIVE_INFINITY];

const labelFor = (n: number) =>
  n === Number.POSITIVE_INFINITY ? "∞" : String(n);

const valueFor = (n: number) =>
  n === Number.POSITIVE_INFINITY ? "inf" : String(n);

const ThresholdSegmented: React.FC<ThresholdSegmentedProps> = ({
  threshold,
  onChange,
  options = DEFAULT_OPTIONS,
}) => {
  const { t } = useTranslation("polls");
  return (
    <ToggleGroup
      type="single"
      value={valueFor(threshold)}
      onValueChange={(v) => {
        if (!v) return;
        onChange(v === "inf" ? Number.POSITIVE_INFINITY : Number(v));
      }}
      data-testid="threshold-segmented"
      aria-label={t("results.threshold.groupLabel")}
    >
      {options.map((opt) => (
        <ToggleGroupItem
          key={valueFor(opt)}
          value={valueFor(opt)}
          data-testid={`threshold-${valueFor(opt)}`}
          aria-label={
            opt === Number.POSITIVE_INFINITY
              ? t("results.threshold.noFilter")
              : t("results.threshold.hideIf", { count: opt })
          }
          className="h-11 min-w-[44px] sm:h-10 sm:min-w-0"
        >
          {labelFor(opt)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};

export default ThresholdSegmented;
